import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { loadWebsiteExperience } from '@/lib/publicCatalog';
import { normalizeVisibility } from '@/lib/customerVisibility';

export const SAFE_FALLBACK = {
  brand: { name:'A&O Premium Fabric Care', tagline:'Technology-Driven Fabric Preservation', promise:'From Alpha to Omega — Trusted Care for Every Fabric' },
  hero: { eyebrow:'Walk-in Fabric Care Atelier', line1:'From Alpha to\nOmega.', line2:'Trusted Care for\nEvery Fabric.', description:'Considered care, precise handling and refined presentation for everyday essentials and garments requiring specialist attention.' },
  contact: { phone:'', phone_href:'', email:'', whatsapp_enabled:false },
  branch: { short_location:'Location details temporarily unavailable', address_lines:[], maps_url:'' },
  hours: { summary:'Hours temporarily unavailable' },
  philosophy: { body:'A&O Premium Fabric Care was created around a simple idea: every fabric deserves a considered path. From assessment to final presentation, the experience is designed to feel precise, transparent and considered.' },
  care_tiers: [],
  prices: [],
  visibility: normalizeVisibility({})
};

const Ctx=createContext({data:SAFE_FALLBACK,live:false,loading:true});
const compact=(xs)=>xs.map(v=>typeof v==='string'?v.trim():v).filter(v=>v!==null&&v!==undefined&&v!=='');
const phoneHref=(phone)=>{if(!phone)return ''; const x=String(phone).replace(/[^\d+]/g,''); return x.startsWith('+')?x:(x?`+${x}`:'');};
const cleanBreaks=(v)=>typeof v==='string'?v.replace(/\\n/g,'\n'):v;


function compactHours(summary){
  const text=String(summary||'').trim();
  if(!text) return SAFE_FALLBACK.hours.summary;
  const parts=text.split('·').map(x=>x.trim()).filter(Boolean);
  const parsed=parts.map(x=>{const m=x.match(/^(Sun|Mon|Tue|Wed|Thu|Fri|Sat)\s+(.+)$/i);return m?{day:m[1].slice(0,1).toUpperCase()+m[1].slice(1,3).toLowerCase(),hours:m[2].trim()}:null;});
  if(parsed.length===7 && parsed.every(Boolean)){
    const weekday=parsed.filter(x=>['Mon','Tue','Wed','Thu','Fri','Sat'].includes(x.day));
    if(weekday.length===6 && weekday.every(x=>x.hours===weekday[0].hours)){
      const sun=parsed.find(x=>x.day==='Sun');
      return `Mon–Sat ${weekday[0].hours} · Sun ${sun.hours}`;
    }
  }
  return text;
}

function normalize(payload){
  const presentation=payload?.presentation||{};
  const raw=payload?.branch||{};
  const pBranch=presentation?.branch||{};
  const area=raw.area_name||raw.area||'';
  const city=raw.city_name||raw.city||'';
  const state=raw.state_name||raw.state||'';
  const pin=raw.pincode||raw.postal_code||'';
  const addressParts=compact([raw.address_line1,raw.address_line2,raw.landmark,area,city,state,pin]);
  const addressLines=compact([raw.address_line1,raw.address_line2,raw.landmark,compact([area,city]).join(', '),compact([state,pin]).join(' ')]);
  const shortLocation=compact([area||raw.branch_name||raw.name,city,pin]).join(', ');
  const phone=raw.phone||presentation?.contact?.phone||'';
  return {
    ...SAFE_FALLBACK,...payload,...presentation,
    brand:{...SAFE_FALLBACK.brand,...(presentation.brand||{})},
    hero:{...SAFE_FALLBACK.hero,...(presentation.hero||{}),line1:cleanBreaks((presentation.hero||{}).line1||SAFE_FALLBACK.hero.line1),line2:cleanBreaks((presentation.hero||{}).line2||SAFE_FALLBACK.hero.line2)},
    philosophy:{...SAFE_FALLBACK.philosophy,...(presentation.philosophy||{})},
    contact:{...(presentation.contact||{}),phone,phone_href:phoneHref(phone),email:raw.email||presentation?.contact?.email||'',whatsapp_enabled:Boolean(presentation?.contact?.whatsapp_enabled)},
    branch:{...pBranch,id:raw.id||raw.branch_id||pBranch.id,code:raw.code||raw.branch_code||pBranch.code,name:raw.name||raw.branch_name||pBranch.name,short_location:shortLocation||pBranch.short_location||SAFE_FALLBACK.branch.short_location,address_lines:addressLines.length?addressLines:(pBranch.address_lines||[]),maps_url:addressParts.length?`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts.join(', '))}`:(pBranch.maps_url||'')},
    hours:{...SAFE_FALLBACK.hours,...(presentation.hours||payload?.hours||{}),summary:compactHours((presentation.hours||payload?.hours||{}).summary)},
    care_tiers:Array.isArray(payload?.care_tiers)?payload.care_tiers:[],
    prices:Array.isArray(payload?.prices)?payload.prices:[],
    visibility:normalizeVisibility(payload||{})
  };
}

export function WebsiteExperienceProvider({children}){
  const [state,setState]=useState({data:SAFE_FALLBACK,live:false,loading:true});
  useEffect(()=>{let active=true; loadWebsiteExperience().then(payload=>{if(active)setState({data:normalize(payload||{}),live:true,loading:false});}).catch(()=>active&&setState({data:SAFE_FALLBACK,live:false,loading:false})); return()=>{active=false};},[]);
  const value=useMemo(()=>state,[state]); return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
export const useWebsiteExperience=()=>useContext(Ctx);
